import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { GetServerInstanceData } from '@/services/inference.gen'
import { PencilLine, Clock } from 'lucide-react'
import { EditNameDialog } from './EditNameDialog'
import { useUpdateServerInstance } from '@/services/server'
import { cn } from '@/lib/utils'
import { toast } from 'react-hot-toast'
import dayjs from 'dayjs'

export interface ServerDetailProps {
  serverInstance: GetServerInstanceData
  serverId: number
  status?: string
  templateName?: string
  uptime: string
  pricingInfo: {
    hourlyRate: string
    totalCost: string
  }
  createdDate: string
  onAutoShutdownClick: () => void
}

type EditState = {
  isEditing: boolean
  tempName: string
}

export const ServerDetailHeader = ({
  serverInstance,
  serverId,
  status,
  templateName,
  uptime,
  pricingInfo,
  createdDate,
  onAutoShutdownClick,
}: ServerDetailProps) => {
  const { instance_name, image, email } = serverInstance
  const { hourlyRate, totalCost } = pricingInfo
  const statusValue = status || 'unknown'
  const templateNameValue = templateName || image

  const [editState, setEditState] = useState<EditState>({
    isEditing: false,
    tempName: instance_name || '',
  })

  const updateMutation = useUpdateServerInstance()

  const onEditNameClick = () => {
    setEditState({
      isEditing: true,
      tempName: instance_name || '',
    })
  }

  const onEditNameSubmit = () => {
    const { tempName } = editState
    if (tempName && tempName !== instance_name) {
      updateMutation.mutate(
        { instance_id: serverId, instance_name: tempName },
        {
          onSuccess: () => {
            setEditState((prev) => ({ ...prev, isEditing: false }))
          },
          onError: (error) => {
            console.error(error)
            toast.error(JSON.stringify(error))
          },
        }
      )
    }
    setEditState((prev) => ({ ...prev, isEditing: false }))
  }

  const onEditNameCancel = () => {
    setEditState({
      isEditing: false,
      tempName: instance_name || '',
    })
  }

  return (
    <>
      <div className="flex justify-between items-start">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold">
              {instance_name ?? 'No instance name'}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEditNameClick}
              className="h-auto p-1.5 text-foreground/60 hover:text-foreground hover:bg-foreground/5"
            >
              <PencilLine size={18} />
            </Button>
          </div>

          <Badge
            variant="outline"
            className={cn(
              statusValue === 'running'
                ? 'bg-primary/10 text-primary'
                : statusValue === 'stopped'
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-foreground/10 text-foreground/80'
            )}
          >
            {statusValue} • {uptime}
          </Badge>
        </div>

        <div className="text-right space-y-1">
          <div className="flex items-center justify-end gap-1">
            <p className="text-lg font-semibold">${totalCost}</p>
            {statusValue === 'running' && (
              <span
                className="h-2 w-2 rounded-full bg-emerald-600 animate-pulse"
                title="Cost updating in real-time"
              ></span>
            )}
          </div>
          <p className="text-foreground/60">${hourlyRate}/hr</p>
        </div>
      </div>

      <div className="mt-6 space-y-2 text-sm">
        <div className="flex gap-2">
          <span className="text-foreground/60">Template:</span>
          <span className="font-medium">{templateNameValue ?? 'N/A'}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-foreground/60">Created By:</span>
          <span className="font-medium">{email}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-foreground/60">Created On:</span>
          <span className="font-medium">{createdDate}</span>
        </div>
        {serverInstance.destroyed_at && (
          <div className="flex gap-2 items-center">
            <span className="text-foreground/60">Shut Down:</span>
            <span className="font-medium text-destructive">
              {dayjs(serverInstance.destroyed_at).format(
                'MMMM DD, YYYY [at] h:mm A'
              )}
            </span>
          </div>
        )}
        {!serverInstance.destroyed_at && serverInstance.auto_shutdown_at && (
          <div className="flex gap-2 items-center">
            <span className="text-foreground/60">Auto-Shutdown:</span>
            <Badge
              variant="outline"
              className="bg-amber-500/10 text-amber-600 border-amber-500/30 flex items-center gap-1 cursor-pointer hover:bg-amber-500/20"
              onClick={onAutoShutdownClick}
            >
              <Clock size={12} />
              {dayjs(serverInstance.auto_shutdown_at).format(
                'MMM DD [at] h:mm A'
              )}
            </Badge>
          </div>
        )}
      </div>

      <EditNameDialog
        isOpen={editState.isEditing}
        onClose={onEditNameCancel}
        onSubmit={onEditNameSubmit}
        initialValue={editState.tempName}
        onChange={(value) =>
          setEditState((prev) => ({ ...prev, tempName: value }))
        }
      />
    </>
  )
}
